import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';

// 에러 타입 정의
export const ERROR_TYPES = {
  NO_CAPTIONS: 'NO_CAPTIONS',
  EXTRACTION_FAILED: 'EXTRACTION_FAILED', 
  UNSUPPORTED_LANGUAGE: 'UNSUPPORTED_LANGUAGE',
  DUPLICATE_VIDEO: 'DUPLICATE_VIDEO',
  ANALYSIS_IN_PROGRESS: 'ANALYSIS_IN_PROGRESS',
  INVALID_URL: 'INVALID_URL',
  VIDEO_NOT_FOUND: 'VIDEO_NOT_FOUND',
  LIVE_STREAM: 'LIVE_STREAM',
  YOUTUBE_SHORTS: 'YOUTUBE_SHORTS',
  YOUTUBE_TIMEOUT: 'YOUTUBE_TIMEOUT', // 🔥 YouTube 15초 타임아웃 에러 추가
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};



const ErrorModal = ({ 
  open, 
  onClose, 
  errorType = ERROR_TYPES.UNKNOWN_ERROR,
  onRetry = null,
  customMessage = null,
  failedHistoryId = null
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // 에러 타입별 설정
  const getErrorConfig = (errorType) => {
    const errorTypeMap = {
      [ERROR_TYPES.NO_CAPTIONS]: 'noCaptions',
      [ERROR_TYPES.EXTRACTION_FAILED]: 'extractionFailed',
      [ERROR_TYPES.UNSUPPORTED_LANGUAGE]: 'unsupportedLanguage',
      [ERROR_TYPES.DUPLICATE_VIDEO]: 'duplicateVideo',
      [ERROR_TYPES.ANALYSIS_IN_PROGRESS]: 'analysisInProgress',
      [ERROR_TYPES.INVALID_URL]: 'invalidUrl',
      [ERROR_TYPES.VIDEO_NOT_FOUND]: 'videoNotFound',
      [ERROR_TYPES.LIVE_STREAM]: 'liveStream',
      [ERROR_TYPES.YOUTUBE_SHORTS]: 'youtubeShorts',
      [ERROR_TYPES.YOUTUBE_TIMEOUT]: 'youtubeTimeout', // 🔥 YouTube 타임아웃 매핑 추가
      [ERROR_TYPES.NETWORK_ERROR]: 'networkError',
      [ERROR_TYPES.SERVER_ERROR]: 'serverError',
      [ERROR_TYPES.UNKNOWN_ERROR]: 'unknownError'
    };

    const errorKey = errorTypeMap[errorType] || 'unknownError';

    const showRetry = [
      ERROR_TYPES.EXTRACTION_FAILED,
      ERROR_TYPES.YOUTUBE_TIMEOUT, // 🔥 YouTube 타임아웃도 재시도 가능
      ERROR_TYPES.NETWORK_ERROR,
      ERROR_TYPES.SERVER_ERROR,
      ERROR_TYPES.UNKNOWN_ERROR
    ].includes(errorType);

    const getSeverity = (type) => {
      if ([ERROR_TYPES.INVALID_URL, ERROR_TYPES.VIDEO_NOT_FOUND, ERROR_TYPES.UNKNOWN_ERROR].includes(type)) {
        return 'error';
      }
      if ([ERROR_TYPES.EXTRACTION_FAILED, ERROR_TYPES.UNSUPPORTED_LANGUAGE, ERROR_TYPES.LIVE_STREAM, ERROR_TYPES.YOUTUBE_TIMEOUT, ERROR_TYPES.NETWORK_ERROR, ERROR_TYPES.SERVER_ERROR].includes(type)) {
        return 'warning';
      }
      return 'info';
    };

    const getIcon = (type) => {
      const severity = getSeverity(type);
      if (severity === 'error') return <ErrorIcon color="error" sx={{ fontSize: 48 }} />;
      if (severity === 'warning') {
        if ([ERROR_TYPES.EXTRACTION_FAILED, ERROR_TYPES.YOUTUBE_TIMEOUT, ERROR_TYPES.NETWORK_ERROR, ERROR_TYPES.SERVER_ERROR].includes(type)) {
          return <RefreshIcon color="warning" sx={{ fontSize: 48 }} />;
        }
        return <WarningIcon color="warning" sx={{ fontSize: 48 }} />;
      }
      return <InfoIcon color="info" sx={{ fontSize: 48 }} />;
    };

    return {
      icon: getIcon(errorType),
      title: t(`error.${errorKey}.title`),
      message: t(`error.${errorKey}.message`),
      description: t(`error.${errorKey}.description`),
      severity: getSeverity(errorType),
      showRetry,
      buttonText: t(`error.${errorKey}.button`)
    };
  };

  const config = getErrorConfig(errorType);
  
  const handleAction = () => {
    // 분석 진행 중이거나 중복 영상인 경우 해당 히스토리 페이지로 이동
    if ((errorType === ERROR_TYPES.ANALYSIS_IN_PROGRESS || errorType === ERROR_TYPES.DUPLICATE_VIDEO) && failedHistoryId) {
      onClose();
      navigate(`/analyze/${failedHistoryId}?fromError=true`);
      return;
    }
    
    if (config.showRetry && onRetry) {
      onRetry();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxWidth: '480px'
        }
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          {config.icon}
        </Box>
        
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            mb: 2,
            color: 'text.primary'
          }}
        >
          {config.title}
        </Typography>
        
        <Alert 
          severity={config.severity} 
          sx={{ 
            mb: 2, 
            textAlign: 'left',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            {customMessage || config.message}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {config.description}
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        {config.showRetry && onRetry && (
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              mr: 1,
              borderColor: '#e0e0e0',
              color: '#666',
              '&:hover': {
                borderColor: '#bdbdbd',
                bgcolor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            {t('drawer.cancel')}
          </Button>
        )}
        <Button
          onClick={handleAction}
          variant="contained"
          startIcon={config.showRetry && onRetry ? <RefreshIcon /> : null}
          sx={{
            borderRadius: 2,
            px: 4,
            bgcolor: config.severity === 'error' ? '#f44336' : 
                     config.severity === 'warning' ? '#ff9800' : '#1976d2',
            '&:hover': {
              bgcolor: config.severity === 'error' ? '#d32f2f' : 
                       config.severity === 'warning' ? '#f57c00' : '#1565c0'
            }
          }}
        >
          {config.buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorModal; 